import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrown,
  faArrowTrendUp,
  faCalendarDay,
  faShieldHalved,
  faPercent,
  faCircleInfo,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { getPartnerDiscount } from "../store/actions";
import { resolveVipState, tierStyle, fmtUsd } from "../components/vip/vipTierUtils";

// Dedicated VIP Tiers page: full explainer (rolling window, T+1, grace) +
// detailed ladder + live status. The dashboard keeps the compact fancy card.
const TEXT = "#e6eae7";
const MUTED = "#7c847e";
const BODY = "#a7b2ab";

const HowCard = ({ icon, color, title, children }) => (
  <div className="panel" style={{ padding: 18 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          color,
          background: `${color}1a`,
          border: `1px solid ${color}40`,
        }}
      >
        <FontAwesomeIcon icon={icon} />
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>{title}</span>
    </div>
    <div style={{ fontSize: 13, lineHeight: 1.65, color: BODY }}>{children}</div>
  </div>
);

const VipTiers = () => {
  const dispatch = useDispatch();
  const pd = useSelector((state) => state.userReducer.partnerDiscount);

  useEffect(() => {
    dispatch(getPartnerDiscount());
  }, [dispatch]);

  const vip = resolveVipState(pd);
  const cur = vip && vip.badgeIdx >= 0 ? tierStyle(vip.badgeIdx) : null;
  const accent = cur ? cur.color : "#3ad17e";

  return (
    <>
      <Helmet>
        <title>VIP Tiers | Instaswap Partners</title>
      </Helmet>

      <div className="secHdr">
        <h1 className="secTitle">
          <FontAwesomeIcon icon={faCrown} style={{ color: accent, marginRight: 12 }} />
          VIP Tiers
        </h1>
        <p className="secLead">
          The more volume you route through InstaSwap, the less you pay. Your trailing 30-day
          volume automatically unlocks a percentage discount on the InstaSwap fee, on every
          route and every product.
        </p>
      </div>

      {/* Live status hero */}
      {vip ? (
        <div
          className="panel"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 30,
            flexWrap: "wrap",
            background:
              `radial-gradient(520px 170px at 8% -30%, ${accent}22, transparent 60%),` +
              `radial-gradient(420px 200px at 100% 140%, ${accent}14, transparent 65%)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span
              style={{
                width: 54,
                height: 54,
                borderRadius: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 23,
                color: accent,
                background: `${accent}1a`,
                border: `1px solid ${accent}45`,
                boxShadow: `0 0 22px ${accent}35`,
              }}
            >
              <FontAwesomeIcon icon={cur ? cur.icon : faCrown} />
            </span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: MUTED }}>
                YOUR TIER
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: cur ? accent : TEXT }}>
                {cur ? cur.name : "No tier yet"}
              </div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: MUTED }}>
              FEE DISCOUNT
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: accent }}>{vip.discountPct}%</div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: MUTED }}>
              30-DAY VOLUME
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: TEXT }}>{fmtUsd(vip.vol30)}</div>
          </div>
          {vip.next && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: MUTED }}>
                {vip.inGrace ? "KEEP YOUR TIER" : "NEXT MILESTONE"}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: BODY, marginTop: 4 }}>
                <b style={{ color: TEXT }}>{fmtUsd(vip.remaining)}</b> more →{" "}
                <b style={{ color: tierStyle(vip.currentIdx + 1).color }}>
                  {tierStyle(vip.currentIdx + 1).name} · {vip.next.discountPct}% off
                </b>
              </div>
            </div>
          )}
          {vip.graceUntil && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12.5,
                color: "#e6c06a",
                background: "rgba(230,192,106,.08)",
                border: "1px solid rgba(230,192,106,.3)",
                borderRadius: 10,
                padding: "8px 12px",
              }}
            >
              <FontAwesomeIcon icon={faShieldHalved} />
              Tier protection active until {String(vip.graceUntil).slice(0, 10)}
            </div>
          )}
        </div>
      ) : (
        <div className="panel" style={{ display: "flex", alignItems: "center", gap: 10, color: BODY, fontSize: 13.5 }}>
          <FontAwesomeIcon icon={faCircleInfo} style={{ color: MUTED }} />
          Your VIP status will appear here once volume data is available for your account.
        </div>
      )}

      {/* How it works */}
      <h2 className="secH">How it works</h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
        }}
      >
        <HowCard icon={faArrowTrendUp} color="#3ad17e" title="Rolling 30-day volume">
          We count the total USD volume you routed through InstaSwap over the{" "}
          <b style={{ color: TEXT }}>last 30 days</b>, across swaps and trading, all routes
          combined. The window slides forward every day: new volume comes in, volume older
          than 30 days drops out. There is no monthly reset, you never start from zero.
        </HowCard>
        <HowCard icon={faCalendarDay} color="#7cc4ff" title="Updated daily, applied next day">
          Your tier is re-evaluated <b style={{ color: TEXT }}>once a day</b>. When your
          30-day volume crosses a milestone, the new discount takes effect{" "}
          <b style={{ color: TEXT }}>the next day</b> and applies automatically to everything
          you route. No codes, no requests, no renegotiation.
        </HowCard>
        <HowCard icon={faShieldHalved} color="#e6c06a" title="7-day tier protection">
          Upgrades are instant, downgrades are cushioned. If your volume dips below your
          tier, you <b style={{ color: TEXT }}>keep your discount for 7 more days</b>. Recover
          within that window and nothing changes. A slow week never costs you your tier.
        </HowCard>
        <HowCard icon={faPercent} color="#c9a2ff" title="Discount on the InstaSwap fee">
          The percentage is taken off the <b style={{ color: TEXT }}>InstaSwap fee</b> on
          every route, including custom and privacy routes. Your own partner fee is never
          touched: you either pass the savings to your users or keep a wider margin.
        </HowCard>
      </div>

      {/* Tier table */}
      {vip && vip.tiers.length > 0 && (
        <>
          <h2 className="secH">Tier milestones</h2>
          <div className="panel" style={{ padding: "8px 20px" }}>
            {vip.tiers.map((t, i) => {
              const st = tierStyle(i);
              const achieved = vip.badgeIdx >= i;
              const isCurrent = vip.badgeIdx === i;
              return (
                <div
                  key={t.minVolumeUsd}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    rowGap: 8,
                    gap: 14,
                    padding: "14px 4px",
                    borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,.05)",
                    opacity: achieved || vip.badgeIdx + 1 === i ? 1 : 0.62,
                  }}
                >
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 11,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      color: achieved ? "#0d1512" : st.color,
                      background: achieved ? st.color : `${st.color}14`,
                      border: `1px solid ${st.color}55`,
                      boxShadow: isCurrent ? `0 0 14px ${st.color}70` : "none",
                      flex: "none",
                    }}
                  >
                    <FontAwesomeIcon icon={st.icon} />
                  </span>
                  <div style={{ minWidth: 74 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: st.color }}>{st.name}</div>
                    {isCurrent && (
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#3ad17e" }}>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: 4 }} />
                        You are here
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, fontSize: 13, color: BODY }}>
                    <b style={{ color: TEXT }}>{fmtUsd(t.minVolumeUsd)}</b>+ volume in 30 days
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: st.color,
                      background: `${st.color}14`,
                      border: `1px solid ${st.color}40`,
                      borderRadius: 999,
                      padding: "5px 14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.discountPct}% off
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Volume stats */}
      {vip && (
        <>
          <h2 className="secH">Your volume</h2>
          <div className="kRow">
            {[
              { label: "Today", value: fmtUsd(vip.volumes.daily) },
              { label: "This month", value: fmtUsd(vip.volumes.monthly) },
              { label: "Rolling 30 days", value: fmtUsd(vip.vol30), hot: true },
              { label: "All time", value: fmtUsd(vip.volumes.allTime) },
            ].map((k) => (
              <div className="kCard" key={k.label} style={k.hot ? { border: `1px solid ${accent}45` } : undefined}>
                <div className="kTop">
                  <span className="kLbl">{k.label}</span>
                  {k.hot && (
                    <span style={{ fontSize: 10, fontWeight: 800, color: accent, letterSpacing: ".06em" }}>
                      COUNTS FOR TIER
                    </span>
                  )}
                </div>
                <div className="kVal">{k.value}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Fine print */}
      <div style={{ marginTop: 22, fontSize: 12, lineHeight: 1.7, color: MUTED, maxWidth: 760 }}>
        Volume counts settled orders attributed to your partner account (API and website
        referral traffic, including sub-partner activity), measured in USD. Tiers are
        evaluated once per day; changes take effect the following day. The discount applies
        to the InstaSwap fee component only. Tier thresholds and percentages may be adjusted;
        the ladder shown here is always the one in force for your account.
      </div>
    </>
  );
};

export default VipTiers;
